import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Pencil, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/partners`;
const EMPTY = { name: '', category: 'Supermarket', discount: { en: '', bn: '' }, code: '', logo_url: '', website: '', active: true };
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}`, 'Content-Type': 'application/json' });

const PartnerForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card className="border-party-green/40" data-testid="partner-form">
      <CardContent className="grid gap-3 p-4 sm:grid-cols-2">
        <Input placeholder="Partner name (e.g. Shwapno)" value={f.name} onChange={set('name')} data-testid="partner-name" />
        <Input placeholder="Category (Supermarket, Lifestyle…)" value={f.category} onChange={set('category')} data-testid="partner-category" />
        <Input placeholder="Discount (English) e.g. 5% off groceries" value={f.discount.en} onChange={(e) => setF({ ...f, discount: { ...f.discount, en: e.target.value } })} data-testid="partner-discount-en" />
        <Input placeholder="Discount (বাংলা)" value={f.discount.bn} onChange={(e) => setF({ ...f, discount: { ...f.discount, bn: e.target.value } })} data-testid="partner-discount-bn" />
        <Input placeholder="Discount code (e.g. NDPSHWAPNO5)" value={f.code} onChange={set('code')} data-testid="partner-code" />
        <Input placeholder="Logo URL (optional)" value={f.logo_url || ''} onChange={set('logo_url')} data-testid="partner-logo" />
        <Input placeholder="Website (optional)" value={f.website || ''} onChange={set('website')} data-testid="partner-website" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="partner-active" /> Active (visible to members)</label>
        <div className="flex gap-2 sm:col-span-2">
          <Button className="bg-party-green hover:bg-green-700" disabled={!f.name || !f.code || !f.discount.en} onClick={() => onSave(f)} data-testid="partner-save"><Save className="mr-2 h-4 w-4" /> Save</Button>
          <Button variant="outline" onClick={onCancel} data-testid="partner-cancel"><X className="mr-2 h-4 w-4" /> Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function ManagePartners() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => fetch(API, { headers: auth() }).then((r) => { if (r.status === 401 || r.status === 403) navigate('/admin/login'); return r.json(); }).then((d) => Array.isArray(d) && setRows(d));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (f) => {
    const { _id, created_at, ...body } = f;
    const res = await fetch(_id ? `${API}/${_id}` : API, { method: _id ? 'PUT' : 'POST', headers: auth(), body: JSON.stringify({ ...body, logo_url: body.logo_url || null, website: body.website || null }) });
    if (res.ok) { toast.success('Partner saved'); setEditing(null); load(); } else toast.error('Save failed');
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    await fetch(`${API}/${id}`, { method: 'DELETE', headers: auth() });
    load();
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="manage-partners-page">
      <header className="bg-white shadow-md">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="mr-2 h-4 w-4" /> Dashboard</Button>
            <h1 className="text-2xl font-bold text-gray-900">Partner Deals</h1>
          </div>
          <Button className="bg-party-red hover:bg-red-700" onClick={() => setEditing(EMPTY)} data-testid="add-partner-btn"><Plus className="mr-2 h-4 w-4" /> Add Partner</Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-8">
        <p className="text-sm text-gray-500">Only add partners once the deal is signed. Discount codes are shown only to <b>verified (approved)</b> members on their ID card page.</p>
        {editing && <PartnerForm key={editing._id || 'new'} initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
        {rows.length === 0 && !editing && <p className="py-10 text-center text-gray-500" data-testid="partners-empty">No partners yet.</p>}
        {rows.map((p) => (
          <Card key={p._id} data-testid={`partner-row-${p._id}`}>
            <CardContent className="flex flex-wrap items-center gap-4 p-4">
              {p.logo_url ? <img src={p.logo_url} alt="" className="h-12 w-12 rounded object-contain" /> : <div className="flex h-12 w-12 items-center justify-center rounded bg-party-red/10 font-bold text-party-red">{p.name[0]}</div>}
              <div className="min-w-0 flex-1">
                <p className="font-bold">{p.name} <span className="text-xs font-normal text-gray-400">{p.category}</span></p>
                <p className="text-sm text-gray-600">{p.discount.en}</p>
              </div>
              <span className="rounded border border-dashed px-2 py-1 font-mono text-sm">{p.code}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>{p.active ? 'Active' : 'Hidden'}</span>
              <Button size="sm" variant="outline" onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`edit-partner-${p._id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="destructive" onClick={() => remove(p._id)} data-testid={`delete-partner-${p._id}`}><Trash2 className="h-4 w-4" /></Button>
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
}
